import { useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard, ShieldCheck, Users, LogOut,
  ChevronRight, Lock, ExternalLink, Menu, X,
} from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import OwnerDashboard from '@/pages/OwnerDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import GuestPortal from '@/pages/GuestPortal';

const queryClient = new QueryClient();

// ─── Types ────────────────────────────────────────────────────────────────────

type Role = 'owner' | 'admin' | 'guest';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'My Access', path: '/owner', icon: LayoutDashboard },
  { label: 'Committee', path: '/admin', icon: ShieldCheck },
  { label: 'Guest View', path: '/guest', icon: Users },
];

const ROLE_MAP: Record<string, Role> = {
  '/owner': 'owner',
  '/admin': 'admin',
  '/guest': 'guest',
};

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Lot 12 · Mike Deggs',
  admin: 'Owners Corporation',
  guest: 'Public Portal Preview',
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ mobileOpen, onClose }: { mobileOpen: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRole = ROLE_MAP[location.pathname] ?? 'owner';

  function goTo(path: string) {
    navigate(path);
    onClose();
  }

  return (
    <>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-20 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <aside
        className={`fixed top-0 left-0 h-full z-30 flex flex-col w-64 transition-transform duration-300 lg:relative lg:translate-x-0 lg:flex lg:z-auto ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: '#26324a', borderRight: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-6 border-b border-white/10">
          <div
            className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: '#458eae', boxShadow: '0 4px 14px rgba(69,142,174,0.4)' }}
          >
            <Lock className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-white font-bold text-sm leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              The Smarter Lock
            </div>
            <div className="text-white/40 text-xs">Canopy Apartments</div>
          </div>
          <button className="ml-auto text-white/40 hover:text-white lg:hidden" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* View-as label */}
        <div className="px-5 pt-5 pb-2">
          <div className="text-white/30 text-xs font-semibold uppercase tracking-widest">View As</div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 space-y-1">
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => goTo(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 group text-left ${
                  active
                    ? 'text-white'
                    : 'text-white/60 hover:text-white'
                }`}
                style={active ? { background: 'rgba(255,255,255,0.1)' } : undefined}
                onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <item.icon className={`h-4 w-4 shrink-0 ${active ? 'text-[#458eae]' : 'text-white/40 group-hover:text-white/70'}`} />
                <span style={{ fontFamily: 'Montserrat, sans-serif' }}>{item.label}</span>
                {active && <ChevronRight className="h-3.5 w-3.5 ml-auto text-white/40" />}
              </button>
            );
          })}
        </nav>

        {/* Current role info */}
        <div className="px-3 py-4 border-t border-white/10">
          <div className="rounded-xl px-4 py-3 space-y-1" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="text-white/50 text-xs uppercase tracking-wider">Current View</div>
            <div className="text-white text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {currentRole === 'owner' ? 'Owner / Resident' : currentRole === 'admin' ? 'Committee / Admin' : 'Guest (Public)'}
            </div>
            <div className="text-white/40 text-xs">{ROLE_DESCRIPTIONS[currentRole]}</div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-white/25 text-xs px-1">
            <ExternalLink className="h-3 w-3" />
            <span>Demo mode — no backend</span>
          </div>
        </div>
      </aside>
    </>
  );
}

// ─── Top Bar ──────────────────────────────────────────────────────────────────

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const location = useLocation();
  const currentRole = ROLE_MAP[location.pathname] ?? 'owner';

  const titles: Record<Role, string> = {
    owner: 'Owner / Resident Portal',
    admin: 'Committee / Admin Portal',
    guest: 'Guest Portal',
  };

  return (
    <header className="flex items-center gap-4 px-4 lg:px-6 h-14 border-b border-border bg-card/80" style={{ backdropFilter: 'blur(8px)' }}>
      <button className="lg:hidden text-muted-foreground hover:text-foreground" onClick={onMenuClick} aria-label="Open menu">
        <Menu className="h-5 w-5" />
      </button>
      <h2 className="font-semibold text-sm text-foreground" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        {titles[currentRole]}
      </h2>
      <div className="ml-auto flex items-center gap-3">
        <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted rounded-full px-3 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" />
          Interactive Demo
        </div>
        <button className="text-muted-foreground hover:text-foreground transition-colors" title="Logout (demo)">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

// ─── Role Pill Bar (used on guest page) ──────────────────────────────────────

function RolePillBar() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div
      className="flex items-center justify-between px-4 py-2 border-b bg-card"
      style={{ borderColor: '#e1e2e6' }}
    >
      <div className="flex items-center gap-2">
        <div className="h-6 w-6 rounded-lg flex items-center justify-center" style={{ background: '#26324a' }}>
          <Lock className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-xs font-bold" style={{ fontFamily: 'Montserrat, sans-serif', color: '#26324a' }}>
          The Smarter Lock
        </span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground mr-1">View:</span>
        {NAV_ITEMS.map(item => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className="px-2 py-0.5 rounded-full text-xs transition-all"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              background: location.pathname === item.path ? '#26324a' : 'transparent',
              color: location.pathname === item.path ? 'white' : '#6b7280',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── App Shell ────────────────────────────────────────────────────────────────

function AppShell() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isGuest = location.pathname === '/guest';

  if (isGuest) {
    return (
      <div className="min-h-screen bg-background">
        <RolePillBar />
        <AnimatePresence mode="wait">
          <motion.div key="/guest" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
            <GuestPortal />
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar mobileOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar onMenuClick={() => setMobileMenuOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="h-full"
            >
              <Routes>
                <Route path="/owner" element={<OwnerDashboard />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="*" element={<Navigate to="/owner" replace />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HashRouter>
          <Routes>
            <Route path="/guest" element={<AppShell />} />
            <Route path="/*" element={<AppShell />} />
          </Routes>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
